const blogPosts = [
  {
    id: 1,
    slug: "our-albania",
    category: "Travel",
    title: "Our Albania",
    intro: "When I was little, seeing a foreigner in our town was an event. Now I spend my days welcoming people from those same places. That still surprises me sometimes.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80",
    date: "March 2025",
    readTime: "4 min read",
    content: `When I was little, seeing a foreigner in our town was an event. We noticed immediately. Someone would whisper, "Where is he from?" and we would all look a little longer than necessary.

It felt rare. Like the outside world existed somewhere far away from us.

Now I spend my days welcoming people from those same places. That still surprises me sometimes.

The change happened fast. One generation ago we were the curious ones. Now we're the ones being explored. We didn't learn tourism slowly. We learned it quickly, sometimes mid-season.

Most guests land in Tirana first.

Tirana doesn't ease you in gently. It's layered, restless, full of opinions. Traffic moves through eye contact and instinct. Surprisingly, it works most of the time. Cafés are full late into the night. On a Tuesday. For no clear reason. It's not polished, but it's warm. It's where coast, mountains, ambition, and memory all cross paths.

From there, the country starts to shift.

Drive out of the city and the rhythm shifts without announcement. The sea opens up. The hills tighten. If you pass through Pogradec, the lake flattens everything into calm. The volume lowers. Even your shoulders drop a little.

Nothing dramatic happens. It just changes.

Nothing here is very far. But everything feels different.

Over the years, I've seen guests try to understand Albania in their first two days. Albania usually ignores that deadline. They loosen their plans. They drive more. They stop longer than intended.

That's when something clicks.

The country is still growing. You can feel it. Roads improve. Expectations rise. Sometimes faster than our infrastructure can keep up. It's not always smooth, but it's honest.

What hasn't changed is the instinct to welcome.

If you spend a few days here, something subtle happens. The need to understand everything fades. You stop measuring the country against somewhere else. You stop asking whether it's organized enough or polished enough.

You just start being here.

And if you ever find yourself sitting somewhere unexpected, holding a small glass of rakia you didn't ask for, wondering how you ended up there… you'll know you're experiencing it properly.`
  },
  {
    id: 2,
    slug: "coastal-light",
    category: "Destinations",
    title: "Coastal Light",
    intro: "The Albanian Riviera is our pride. You don't need anyone to explain why. You see it the moment you stand in front of the water. Clear. Steady. Open.",
    image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80",
    date: "February 2025",
    readTime: "4 min read",
    content: `The Albanian Riviera is our pride.

You don't need anyone to explain why. You see it the moment you stand in front of the water. Clear. Steady. Open. A stretch of Mediterranean coastline that feels like it was shaped for slowing down.

It's a relaxed corner of the world, and we've always known it that way. Long evenings. Warm light. Sea that stays impossibly clear. The kind of place where the day doesn't demand anything from you.

Morning begins easily. Coffee outside, the sea already in view. The air warm, the light soft. You sit longer than you planned because there is nowhere else you need to be. That's how it works here.

When you walk into the water, you take your time. The first step is cool, then softer. You dive. For a few seconds everything goes quiet. When you float back up, the horizon feels wider than before.

Lunch stretches without effort. Fish, bread, olive oil poured generously. A cold glass in the shade. Conversation moving slowly, without agenda. No one checking the clock. No one rushing you through the moment.

You drift between sea and shade. Back into the water. Back out again. The afternoon moves gently around you, as if it has all the time in the world.

As the sun lowers, you return to the sea once more. The surface is smoother now. You float on your back and watch the sun sink slowly into the horizon, turning the sky from gold to something deeper and softer.

You stay in until the air cools slightly against your shoulders.

Evening arrives quietly. Skin still warm. Hair still damp. A drink in your hand. Music somewhere behind you, low and unforced.

This coastline has always been a gift to us. And it's a quiet joy to open it to those who come from far away to experience it.

You sit facing the water as the last light disappears.

And the thought comes without effort.

I don't want to leave.`
  },
  {
    id: 3,
    slug: "city-rhythm",
    category: "Culture",
    title: "City Rhythm",
    intro: "Tirana is where Albania meets itself. You can sit down for coffee with four people and realize their parents come from eight different cities. It happens naturally.",
    image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200&q=80",
    date: "January 2025",
    readTime: "4 min read",
    content: `Tirana is where Albania meets itself.

You can sit down for coffee with four people and realize their parents come from eight different cities. Shkodër. Gjirokastër. Kukës. Vlorë. Korçë. Tropojë. Fier. Somewhere in between. It happens naturally. It's just how the city works.

For half a century, Albanians weren't free to move, to gather, to build their lives wherever they wished. Families stayed where they were told to stay. Opportunities were assigned. Movement was restricted.

Tirana today is the quiet opposite of that.

It is the bravest experiment we've ever carried out without calling it one.

A city built by arrivals. By people who left mountains, coasts, small towns and villages, and chose to live closer to one another. Accents mix here. Traditions overlap. Recipes change slightly. Stories are retold with different endings.

You feel it in the cafés first. Tables full from mid-morning. Coffee that stretches longer than logic would allow. Conversations layered over each other. Plans being formed between sips. Debates that are loud but rarely hostile. Someone always joining the table. Someone always staying longer than planned.

The rhythm is social.

Morning coffee is not a break from work. It is part of work. It is how things move. Afternoons slow slightly, but evenings belong to the streets. As the sun lowers, the city steps outside. Boulevards fill. Side streets hum. Music drifts out of open doors. You don't have to look for energy here. You walk into it.

Tirana doesn't try to impress you with perfection.

It is still becoming. Still adjusting to its own growth. Still negotiating how to live so closely after so long apart.

But there is something deeply hopeful about that.

After decades of separation, this is what closeness looks like. A little messy. A little loud. Very alive.

And when you sit at a table here long enough, you stop feeling like a visitor.

You feel from Tirana as well.`
  },
  {
    id: 4,
    slug: "lake-days",
    category: "Destinations",
    title: "Lake Days",
    intro: "Most people arrive in Pogradec carrying noise they don't even realize they've been holding. The lake doesn't push it away. It simply refuses to mirror it.",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80",
    date: "December 2024",
    readTime: "4 min read",
    content: `Most people arrive in Pogradec carrying noise they don't even realize they've been holding.

You feel it in the first hour. The lake doesn't push it away. It simply refuses to mirror it. It stays still.

The water rests between the mountains as if it has always been protected there. The hills soften the wind before it reaches the shore. They hold sound gently. Even your voice lowers without you deciding to.

You sit near the water and notice your shoulders drop. Your jaw relaxes. Breathing becomes slower. Not because you're trying to meditate. Because nothing here asks you to be tense.

The surface of the lake is often calm enough to reflect the sky clearly. A bird crosses it. A fish breaks the water and disappears. Small movements. Quiet ones. You start noticing them.

You begin to notice yourself as well. The way your thoughts slow down. The way your body stops leaning forward. The way you sit without checking what's next.

Time doesn't feel urgent here. It feels wide.

You walk along the shore and hear things you normally miss. The soft rhythm of wings lifting from the water. The gentle contact between waves and stone. Leaves shifting behind you in the trees. Nothing dramatic. Just life continuing without interruption.

Sometimes you do nothing at all. You sit. You look across the water. The mountains hold the horizon in place. Light moves slowly across their surface. You don't need to interpret it.

Pogradec feels slightly removed from the loudness of the world. Not isolated. Just sheltered. The mountains form a natural boundary, as if they agreed long ago to keep certain kinds of noise out.

You don't come here to be entertained.

You come here to breathe properly again.

And when you leave, you realize something simple.

You are carrying less with you.

The lake kept the rest.`
  }
];

export default blogPosts;
